import { Observable, of } from 'rxjs';
import { Injectable, TransferState, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { TServiceResult } from '@shared/helper/t-service-result';
import { LocalRepository } from '@shared/helper/local-repository';
import { HttpBaseService } from '../base_services/http_base.service';
import { FollowsModel } from '@shared/model/followModels/follow-model';
import { PageList } from '@shared/model/categoryAndSearch/pageList-model';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { catchError, share } from 'rxjs/operators';
import { IGetFollowRequest } from '@shared/model/followModels/follow-request.model';
import { ISuggestedUserDto } from '@shared/model/suggested-user/suggested-user.model';
import { activeUserModel } from '@shared/model/customerModels/customer-model';

@Injectable({
  providedIn: 'root'
})

export class FollowAggService extends HttpBaseService {
  private transferState = inject(TransferState);


  constructor() {
    const repo = inject(LocalRepository);
    const http = inject(HttpClient);

    super(repo, http, 'v1/foAggr');
  }

  follow(follow: FollowsModel): Observable<TServiceResult<number>> {
    return this.http.post<TServiceResult<number>>(`${this.apiUrl}/Follow`, follow);
  }

  getGuestFollowersList(userId: string, TF?: TermFilterModel, term?: string): Observable<TServiceResult<PageList<FollowsModel>>> {
    let paramsValues = this.TermFilterParams(TF);
    paramsValues += term?.length! > 0 ? `&SearchTerm=${term}` : '';
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetGuestFollowersList/${userId}/${paramsValues}`);
  }

  getGuestFollowingsList(userId: string, TF?: TermFilterModel, term?: string): Observable<TServiceResult<PageList<FollowsModel>>> {
    let paramsValues = this.TermFilterParams(TF);
    paramsValues += term?.length! > 0 ? `&SearchTerm=${term}` : '';
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetGuestFollowingsList/${userId}/${paramsValues}`);
  }

  getMyFollowersList(TF?: TermFilterModel): Observable<TServiceResult<PageList<FollowsModel>>> {
    let paramsValues = '';
    if (TF?.pgNumber || TF?.pgNumber == 0)
      paramsValues = `?PgNumber=${TF?.pgNumber}`
    if (TF?.pgSize)
      paramsValues = `?PgSize=${TF?.pgSize}`
    if ((TF?.pgNumber || TF?.pgNumber == 0) && TF?.pgSize)
      paramsValues = `?PgNumber=${TF?.pgNumber}&PgSize=${TF?.pgSize}`;
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetMyFollowersList/${paramsValues}`);
  }

  getMyFollowingsList(TF?: TermFilterModel): Observable<TServiceResult<PageList<FollowsModel>>> {
    let paramsValues = '';
    if (TF?.pgNumber || TF?.pgNumber == 0)
      paramsValues = `?PgNumber=${TF?.pgNumber}`
    if (TF?.pgSize)
      paramsValues = `?PgSize=${TF?.pgSize}`
    if ((TF?.pgNumber || TF?.pgNumber == 0) && TF?.pgSize)
      paramsValues = `?PgNumber=${TF?.pgNumber}&PgSize=${TF?.pgSize}`;
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetMyFollowingsList/${paramsValues}`);
  }

  getAuthorFollowingList(authorIdentifier: string, TF?: TermFilterModel): Observable<TServiceResult<PageList<FollowsModel>>> {
    let paramsValues = '';
    if (TF?.pgNumber || TF?.pgNumber == 0)
      paramsValues = `?PgNumber=${TF?.pgNumber}`
    if (TF?.pgSize)
      paramsValues = `?PgSize=${TF?.pgSize}`
    if ((TF?.pgNumber || TF?.pgNumber == 0) && TF?.pgSize)
      paramsValues = `?PgNumber=${TF?.pgNumber}&PgSize=${TF?.pgSize}`;
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetAuthorFollowingsList/${authorIdentifier}/${paramsValues}`);
  }

  getAuthorFollowingsCount(authorIdentifier: string): Observable<TServiceResult<number>> {
    return this.http.get<TServiceResult<number>>(`${this.apiUrl}/GetAuthorFollowingsCount/${authorIdentifier}`)
      .pipe(
        share(),
        catchError(() => {
          return of(new TServiceResult<number>(0, '', null, true, ''))
        })
      )
  }

  getAuthorFollowersCount(authorIdentifier: string): Observable<TServiceResult<number>> {
    return this.http.get<TServiceResult<number>>(`${this.apiUrl}/GetAuthorFollowersCount/${authorIdentifier}`)
      .pipe(
        share()
      )
  }

  getGuestFollowersCount(id: string): Observable<TServiceResult<number>> {
    return this.http.get<TServiceResult<number>>(`${this.apiUrl}/GetGuestFollowersCount/${id}`)
      .pipe(
        share(),
        catchError(() => {
          return of(new TServiceResult<number>(0, '', null, true, ''));
        })
      )
  }

  getGuestChatContacts(currentUserId: string, termFilter: TermFilterModel): Observable<TServiceResult<PageList<FollowsModel>>> {
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetGuestChatContacts/${currentUserId}?SearchTerm=${termFilter.searchTerm}`,);
  }

  getFollowRequests(termFilter: TermFilterModel): Observable<TServiceResult<PageList<IGetFollowRequest>>> {
    let paramsValues = '';
    if (termFilter.pgNumber || termFilter.pgNumber == 0)
      paramsValues = `?PgNumber=${termFilter.pgNumber}`
    if (termFilter.pgSize)
      paramsValues = `?PgSize=${termFilter.pgSize}`
    if ((termFilter.pgNumber || termFilter.pgNumber == 0) && termFilter.pgSize)
      paramsValues = `?PgNumber=${termFilter.pgNumber}&PgSize=${termFilter.pgSize}`;
    return this.http.get<TServiceResult<PageList<IGetFollowRequest>>>(`${this.apiUrl}/GetFollowRequests/${paramsValues}`);
  }

  GetSuggestedUsersToFollow(term: string): Observable<TServiceResult<PageList<ISuggestedUserDto>>> {
    return this.http.get<TServiceResult<PageList<ISuggestedUserDto>>>(`${this.apiUrl}/GetSuggestedUsersToFollow/${term}`)
  }



  getSuggestedBestUsers(termFilter: TermFilterModel): Observable<TServiceResult<PageList<activeUserModel>>> {
    let paramsValues = '';
    if (termFilter.pgNumber || termFilter.pgNumber == 0)
      paramsValues = `?PgNumber=${termFilter.pgNumber}`
    if (termFilter.pgSize)
      paramsValues = `?PgSize=${termFilter.pgSize}`
    if ((termFilter.pgNumber || termFilter.pgNumber == 0) && termFilter.pgSize)
      paramsValues = `?PgNumber=${termFilter.pgNumber}&PgSize=${termFilter.pgSize}`;
    return this.http.get<TServiceResult<PageList<activeUserModel>>>(`${this.apiUrl}/GetSuggestedBestUsers/${paramsValues}`);
  }
}
